<script>
  import { onMount, onDestroy } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  import Pagination from '$lib/Pagination.svelte';
  import { selectedStudy, selectedSite } from '$stores/selectionStore';
  import { refreshCurrentSiteCount, forceRefreshSiteCount } from '$stores/messageCountStore';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import SiteDetails from '$lib/SiteDetails.svelte';
  import Toolbar from '$lib/Toolbar.svelte';
  import Footer from '$lib/Footer.svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faRotate, faChevronRight, faCheckDouble, faBell, faBellExclamation } from '@fortawesome/pro-light-svg-icons';
  import { studyProperties } from '$stores/configStore';

  // Use local API endpoints instead of direct API with key
  const API_BASE_URL = '/api/proxy';
  import { formatToLocalDateTime } from '$utils/dateUtils.js';

  let messages = [];
  let resultCount = 0;
  let currentPage = 1;
  let totalPages = 1;
  let limit = 20;
  let isLoading = true;
  let selectedType = 'ALL';
  let selectedStatus = 'ALL';
  let isMarkingAsRead = false;
  let hasInitialized = false;
  let lastSiteId = null;
  let isTogglingMute = false;
  let showUnmutePopover = false;
  let unmutePopoverContainer;

  const UNMUTE_DAY_OPTIONS = [1, 7, 10, 14, 15, 30];

  $: isMessageMutingEnabled = $studyProperties?.features?.messageMuting === true;
  $: isSiteMuted = $selectedSite?.muted === true;
  $: isTemporarilyUnmuted = $selectedSite?.muted === false
    && $selectedSite?.muteResumeDate
    && new Date($selectedSite.muteResumeDate) > new Date();

  function handleMuteButtonClick() {
    if (!$selectedSite || isTogglingMute) return;
    if (isSiteMuted) {
      showUnmutePopover = !showUnmutePopover;
    } else {
      sendMuteUpdate({ muted: true });
    }
  }

  async function handleUnmuteSelection(days) {
    showUnmutePopover = false;
    const body = days ? { muted: false, unmuteDays: days } : { muted: false };
    await sendMuteUpdate(body);
  }

  async function sendMuteUpdate(body) {
    if (isTogglingMute) return;
    const action = body.muted ? 'mute' : 'unmute';
    try {
      isTogglingMute = true;
      const response = await fetch(`${API_BASE_URL}/sites/${$selectedSite._id}/muted`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!response.ok) throw new Error(`Failed to ${action} site`);

      const result = await response.json();
      const doc = result.data || result;
      $selectedSite = { ...$selectedSite, muted: doc.muted, muteResumeDate: doc.muteResumeDate ?? null };
    } catch (error) {
      console.error('Error toggling muted state:', error);
      alert(`Failed to ${action} site. Please try again.`);
    } finally {
      isTogglingMute = false;
    }
  }

  function handleClickOutsidePopover(event) {
    if (unmutePopoverContainer && !unmutePopoverContainer.contains(event.target)) {
      showUnmutePopover = false;
    }
  }

  // Reactive statement to handle selectedSite changes (only when site actually changes)
  $: if ($selectedSite && $selectedSite._id && hasInitialized && lastSiteId !== $selectedSite._id) {
    lastSiteId = $selectedSite._id;
    // console.log('Site changed, fetching messages for new site:', $selectedSite.siteId);
    fetchMessages(1);
  }

  onMount(async () => {
    document.addEventListener('click', handleClickOutsidePopover);
    if ($selectedSite && !hasInitialized) {
      hasInitialized = true;
      try {
        console.log('Initializing siteMessages for site:', $selectedSite.siteId);
        await fetchMessages(1);
        await refreshCurrentSiteCount();
      } catch (error) {
        console.error('Error in siteMessages onMount:', error);
        isLoading = false;
      }
    } else {
      isLoading = false;
    }
  });

  onDestroy(() => {
    if (typeof window !== 'undefined') {
      document.removeEventListener('click', handleClickOutsidePopover);
    }
  });

  async function fetchMessages(page = 1) {
    try {
      if (!$selectedSite || !$selectedSite._id) {
        console.error('No selected site available for fetching messages');
        messages = [];
        resultCount = 0;
        currentPage = 1;
        totalPages = 1;
        return;
      }

      isLoading = true;
      let url = `${API_BASE_URL}/messages?page=${page}&pageSize=${limit}&siteGUID=${$selectedSite._id}`;

      if (selectedType !== 'ALL') {
        url += `&type=${selectedType}`;
      }

      if (selectedStatus !== 'ALL') {
        url += `&status=${selectedStatus}`;
      }

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error(`Failed to fetch messages: ${res.status} ${res.statusText}`);
      }

      const responseData = await res.json();

      resultCount = responseData.totalCount || 0;
      currentPage = responseData.page || 1;
      totalPages = responseData.totalPages || 1;

      messages = responseData.data || [];
    } catch (err) {
      console.error('Error fetching messages:', err);
      messages = [];
      resultCount = 0;
      currentPage = 1;
      totalPages = 1;
    } finally {
      isLoading = false;
    }
  }

  async function handleRefresh() {
    fetchMessages(1);
    // Also refresh the message count when manually refreshing
    await refreshCurrentSiteCount();
  }

  function handlePageChange(newPage) {
    if (newPage > 0 && newPage <= totalPages) {
      fetchMessages(newPage);
    }
  }

  async function markAllAsRead() {
    if (!$selectedSite) return;

    const confirmed = confirm('Are you sure you want to mark all "Sent" messages as "Read"?');
    if (!confirmed) return;

    try {
      isMarkingAsRead = true;

      const response = await fetch(`${API_BASE_URL}/messages/markAsRead`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          siteGUID: $selectedSite._id,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to mark messages as read');
      }

      const result = await response.json();

      // Show success message
      // alert(`Successfully marked ${result.modifiedCount} messages as read.`);

      // Refresh the messages list and count - handle these separately to avoid masking the success
      try {
        await fetchMessages(1);
      } catch (refreshError) {
        console.error('Error refreshing messages list:', refreshError);
        // Don't show error to user since the main operation succeeded
      }

      try {
        await refreshCurrentSiteCount();
        // Force refresh the sidebar count to ensure it updates immediately
        await forceRefreshSiteCount($selectedSite._id);
      } catch (countError) {
        console.error('Error refreshing message count:', countError);
        // Don't show error to user since the main operation succeeded
      }
    } catch (error) {
      console.error('Error marking messages as read:', error);
      alert('Failed to mark messages as read. Please try again.');
    } finally {
      isMarkingAsRead = false;
    }
  }
</script>

<StudyHeader />

{#if $selectedSite}
  <SiteDetails showOptionsMenu={false} />
{/if}

<Toolbar title="Site Messages" itemCountTitle="Messages" itemCount={resultCount}>
  <div class="flex items-center gap-2">
    <div class="text-m mb-0">
      Type
      <select
        id="type"
        class="form-select h-6 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
        bind:value={selectedType}
      >
        <option value="ALL">All</option>
        <option value="Alert">Alert</option>
        <option value="Reminder">Reminder</option>
        <option value="CGM Recheck">CGM Recheck</option>
      </select>
    </div>
    <div class="text-m mb-0">
      Status
      <select
        id="status"
        class="form-select h-6 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
        bind:value={selectedStatus}
      >
        <option value="ALL">All</option>
        <option value="Sent">Sent</option>
        <option value="Read">Read</option>
      </select>
    </div>
    {#if isMessageMutingEnabled}
      <div class="relative" bind:this={unmutePopoverContainer}>
        <button
          on:click={handleMuteButtonClick}
          class="pagination mx-1 p-0"
          title={isTemporarilyUnmuted
            ? `Temporarily unmuted until ${new Date($selectedSite.muteResumeDate).toLocaleDateString()} — click to re-mute`
            : isSiteMuted
              ? 'Unmute alerts for this site'
              : 'Mute alerts for this site'}
          disabled={isTogglingMute}
        >
          {#if isTemporarilyUnmuted}
            <FontAwesomeIcon icon={faBell} class="{isTogglingMute ? 'animate-pulse' : ''} text-amber-500" />
          {:else}
            <FontAwesomeIcon
              icon={isSiteMuted ? faBellExclamation : faBell}
              class="{isTogglingMute ? 'animate-pulse' : ''} {isSiteMuted ? 'text-red-500' : ''}"
            />
          {/if}
        </button>
        {#if showUnmutePopover}
          <div class="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-10">
            <div class="px-4 py-1 text-xs text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">Unmute for...</div>
            {#each UNMUTE_DAY_OPTIONS as days}
              <button
                on:click={() => handleUnmuteSelection(days)}
                class="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {days} {days === 1 ? 'day' : 'days'}
              </button>
            {/each}
            <div class="border-t dark:border-gray-700"></div>
            <button
              on:click={() => handleUnmuteSelection(null)}
              class="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Permanently
            </button>
          </div>
        {/if}
      </div>
    {/if}
    <button
      on:click={markAllAsRead}
      class="pagination mx-1 p-0"
      title="Mark all Sent messages as Read"
      disabled={isMarkingAsRead}
    >
      <FontAwesomeIcon icon={faCheckDouble} class={isMarkingAsRead ? 'animate-spin' : ''} />
    </button>
    <button on:click={handleRefresh} class="pagination mx-1 p-0" title="Refresh">
      <FontAwesomeIcon icon={faRotate} />
    </button>
  </div>
</Toolbar>

{#if isLoading}
  <div class="flex flex-col justify-center items-center h-64 bg-stone-100/50 dark:bg-gray-725 mx-1">
    <div class="loader mb-4"></div>
    <p class="text-xs justify-center text-gray-700 dark:text-gray-300">preparing messages data</p>
  </div>
{:else if messages.length === 0}
  <div class="flex flex-col justify-center items-center h-64 bg-stone-100/50 dark:bg-gray-725 mx-1">
    <p class="text-sm text-gray-700 dark:text-gray-300">No messages found.</p>
  </div>
{:else}
  <div class="px-1 pt-0">
    <table class="min-w-full border-collapse table-auto">
      <thead>
        <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
          <th class="px-4 py-2 text-left bold">Site ID</th>
          <th class="px-4 py-2 text-left bold">Subject ID</th>
          <th class="px-4 py-2 text-left bold">Title</th>
          <th class="px-4 py-2 text-left bold">Message</th>
          <th class="px-4 py-2 text-left bold">Type</th>
          <th class="px-4 py-2 text-left bold">Status</th>
          <th class="px-4 py-2 text-left bold">Created</th>
          <th class="px-4 py-2 text-left bold">Read</th>
        </tr>
      </thead>
      <tbody>
        {#each messages as message, index}
          <tr
            class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
              2 ===
            0
              ? 'bg-stone-50 dark:bg-gray-725'
              : 'bg-stone-75 dark:bg-gray-750'}"
          >
            <td class="px-4 py-2">{message.siteId}</td>
            <td class="px-4 py-2">{message.subjectId || '—'}</td>
            <td class="px-4 py-2">{message.title}</td>
            <td class="px-4 py-2">{message.message}</td>
            <td class="px-4 py-2">{message.type}</td>
            <td class="px-4 py-2">{message.status}</td>
            <td class="px-4 py-2">{formatToLocalDateTime(message.created)}</td>
            <td class="px-4 py-2">{message.read ? formatToLocalDateTime(message.read) : ''}</td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{/if}

<!-- Pagination -->
<Footer>
  <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
</Footer>

<style>
  .loader {
    border: 2px solid #20669c;
    border-radius: 50%;
    border-top: 4px solid #98acb4;
    width: 32px;
    height: 32px;
    animation: spin 2s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
</style>
